import React, { useEffect, useState, useCallback } from "react";
import './styles.css';
import GetModal from "../api/getModal";
import { AlertsData } from './dashboard';
import { useRoleContext } from "../utils/roleContext";
import NotifyComponent from "../components/notifyComponent";
import { FTPValidation } from "../utils/validateFtpCreds";

// Types and Interfaces
interface DealershipData {
    dealerShipName?: string;
    [key: string]: any;
}

interface DMSFormData {
    dmsId: number;
    companyID: string;
    enterprise: string;
    hostAddress: string;
    lotlocations: string;
    stockInCo: string;
    dealship: DealershipData;
    dmstype: number;
    dealerCode: string;
    enableLotLocationOptionNumber: boolean;
    lotLocationOptionNumber: string;
    lotLocationOptionDescription: string;
    enableLastScannedOptionNumber: boolean;
    lastScannedOptionNumber: string;
    lastScannedOptionDescription: string;
    integrationURLForDominion: string;
    dealerNumberID: string;
    senderNameCode: string;
    destinationNameCode: string;
    authenticationURL: string;
    downloadCustomerVehicles: boolean;
    ignoreZeroBalance: boolean;
    sftpFileBasedDMS: boolean;
    scanSFTPFilebasedSettings: boolean;
    sftpHost: string | null;
    sftpPort: number | 0;
    sftpUserName: string | null;
    sftpPassword: string | null;
    sftpDirectory: string | null;
    scanSFTPHost: string | null;
    scanSFTPPort: number | 0;
    scanSFTPUserName: string | null;
    scanSFTPPassword: string | null;
    scanSFTPDirectory: string | null;
    directorSettings: boolean;
    directorName: string | null;
}

interface MainFTPConfig {
    port: number;
    defaultServer: string;
    connectionSuccess: boolean;
    connectionAttempted: boolean;
}

interface FTPConfig {
    host: string;
    port: number;
    username: string;
    password: string;
    path: string;
}

interface ScanFTPConfig {
    scanhost: string;
    scanport: number;
    scanusername: string;
    scanpassword: string;
    scanpath: string;
}

interface DirectorConfig {
    settings: boolean;
    name: string | null;
}

interface Props {
    onAlertChnage: AlertsData;
}

// Constants
const DMS_TYPES = {
    CDK: 0,
    DEALER_TRACK: 1,
    DOMINION: 2,
    FILE_BASED: 3,
} as const;

const DMS_TYPE_LABELS = {
    [DMS_TYPES.CDK]: 'CDK',
    [DMS_TYPES.DEALER_TRACK]: 'Dealer Track',
    [DMS_TYPES.DOMINION]: 'Dominion',
    [DMS_TYPES.FILE_BASED]: 'File Based',
};

const DEFAULT_FTP_CONFIG: MainFTPConfig = {
    port: 22,
    defaultServer: 'Custom',
    connectionSuccess: false,
    connectionAttempted: false
};

const DEFAULT_SCAN_FTP_CONFIG: ScanFTPConfig = {
    scanhost: '',
    scanport: 22,
    scanusername: '',
    scanpassword: '',
    scanpath: ''
}

const DEFAULT_CUSTOM_FTP_CONFIG: FTPConfig = {
    host: '',
    port: 22,
    username: '',
    password: '',
    path: ''
}

const DEFAULT_DIRECTOR_CONFIG: DirectorConfig = {
    settings: false,
    name: ''
}

const DmsConnectivity: React.FC<Props> = ({ onAlertChnage }) => {
    //Notification States
    const [showNotification, setShowNotification] = useState<boolean>(false);
    const [pendingFormData, setPendingFormData] = useState<DMSFormData | null>(null);
    const [pendingEndPoint, setPendingEndPoint] = useState<string>('');
    const [notificationMessage, setNotificationMessage] = useState<string>('');

    // Core state
    const [dealershipName, setDealershipName] = useState<string>("");
    const [dmsType, setDmsType] = useState<number>(DMS_TYPES.CDK);
    const [dmsId, setDmsId] = useState<number>(0);
    const [dealershipData, setDealershipData] = useState<DealershipData>({});

    // CDK/Dealer Track specific fields
    const [companyID, setCompanyID] = useState<string>('');
    const [enterprise, setEnterprise] = useState<string>('');
    const [hostAddress, setHostAddress] = useState<string>('');

    // Dominion specific fields
    const [integrationUrl, setIntegrationUrl] = useState<string>('');
    const [authUrl, setAuthUrl] = useState<string>('');
    const [dealerNumber, setDealerNumber] = useState<string>('');
    const [senderName, setSenderName] = useState<string>('');
    const [destinationName, setDestinationName] = useState<string>('');

    // Common fields
    const [lotLocations, setLotLocations] = useState<string>('');
    const [stockInCo, setStockInCo] = useState<string>('');
    const [lotLocationOptionNumber, setLotLocationOptionNumber] = useState<string>('');
    const [lotLocationOptionDescription, setLotLocationOptionDescription] = useState<string>('');
    const [lastScannedOptionNumber, setLastScannedOptionNumber] = useState<string>('');
    const [lastScannedOptionDescription, setLastScannedOptionDescription] = useState<string>('');
    const [dealerCode, setDealerCode] = useState<string>('');

    // Checkboxes
    const [downloadCustomerVehicles, setDownloadCustomerVehicles] = useState<boolean>(false);
    const [ignoreZeroBalance, setIgnoreZeroBalance] = useState<boolean>(false);
    const [isFtpEnabled, setIsFtpEnabled] = useState<boolean>(false);
    const [isLotlocationEnabled, setIsLotlocationEnabled] = useState<boolean>(false);
    const [isLastScannedEnabled, setIsLastScannedEnabled] = useState<boolean>(false);

    // FTP Configuration
    const [ftpConfig, setFtpConfig] = useState<MainFTPConfig>(DEFAULT_FTP_CONFIG);
    const [customFTPConfig, setCustomFTPConfig] = useState<FTPConfig>(DEFAULT_CUSTOM_FTP_CONFIG);
    const [scanFTPConfig, setScanFTPConfig] = useState<ScanFTPConfig>(DEFAULT_SCAN_FTP_CONFIG);
    const [scanFTPServer, setScanFTPServer] = useState<boolean>(false);
    const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false);
    const [connectionAttempted, setConnectionAttempted] = useState<boolean>(false);
    const [ftpConnectionErrors, setFtpConnectionErrors] = useState<string>('');

    // Director Configurations 
    const [directorConfig, setDirectorConfig] = useState<DirectorConfig>(DEFAULT_DIRECTOR_CONFIG);

    const dealerId = sessionStorage.getItem('dealerId');
    const { userRole } = useRoleContext();
    const directorName = sessionStorage.getItem('userName');

    // Utility functions
    const sendAlert = useCallback((errorCode: number, message: string, status: string): void => {
        onAlertChnage(errorCode, message, status);
    }, [onAlertChnage]);

    const resetFormFields = useCallback(() => {
        setCompanyID('');
        setEnterprise('');
        setHostAddress('');
        setIntegrationUrl('');
        setAuthUrl('');
        setDealerNumber('');
        setSenderName('');
        setDestinationName('');
    }, []);

    //Notification settings confirmation
    const handleOk = () => {
        setShowNotification(false);

        if (!directorName) {
            console.log('Director name not found');
            return;
        }

        const isDirector = userRole === "Director";

        // Update director config
        setDirectorConfig(prev => ({
            ...prev,
            settings: isDirector,
            name: isDirector ? directorName : null
        }));

        // Process pending API call with updated data
        if (pendingFormData && pendingEndPoint) {
            const updatedFormData = {
                ...pendingFormData,
                directorSettings: isDirector,
                directorName: isDirector ? directorName : null
            };
            handleApiCall(pendingEndPoint, updatedFormData);
        }

        // Clear pending data
        setPendingFormData(null);
        setPendingEndPoint('');
    }

    const handleCancel = () => {
        setShowNotification(false);
        sendAlert(0, 'Settings wont be saved for all dealers as user pressed No', 'Success');
        setPendingFormData(null);
        setPendingEndPoint('');
    }

    const handleFTPServerChange = (value: string) => {
        if (value === 'Scan') {
            setScanFTPServer(true);
            setFtpConfig(prev => ({
                ...prev,
                defaultServer: 'Scan'
            }));
        } else {
            setScanFTPServer(false);
            setFtpConfig(prev => ({
                ...prev,
                defaultServer: 'Custom'
            }));
        }
    }

    const handleTestConnection = async () => {
        setIsTestingConnection(true);
        setConnectionAttempted(true);
        try {
            const isValid = await FTPValidation({
                host: customFTPConfig.host,
                port: customFTPConfig.port,
                user: customFTPConfig.username,
                password: customFTPConfig.password,
                path: customFTPConfig.path,
                caller: 'Test'
            });

            if (isValid) {
                setFtpConfig(prev => ({
                    ...prev,
                    connectionSuccess: true,
                    connectionAttempted: true
                }));
            }
        } catch (error: any) {
            setFtpConfig(prev => ({
                ...prev,
                connectionSuccess: false,
                connectionAttempted: true
            }));
            console.log(error.message)
            setFtpConnectionErrors(error.message);
        } finally {
            setIsTestingConnection(false);
        }
    }

    // Data fetching
    const fetchDealershipData = useCallback(async (): Promise<void> => {
        if (!dealerId) return;

        try {
            const response: any = await GetModal('GET', userRole === "Director" ? `DealerShip/DMSCreate?dealerIdSel=${directorName}-DMSConnectivity` : `DealerShip/DMSCreate?dealerIdSel=${dealerId}-DMSConnectivity`, '');

            // Set core data
            setDmsId(response.dmsId || 0);
            setDealershipData(response.dealship || {});
            setDealershipName(response.dealship?.dealerShipName || "");
            setDmsType(response.dmstype || DMS_TYPES.CDK);

            // Set form fields
            setCompanyID(response.companyID || "");
            setEnterprise(response.enterprise || "");
            setHostAddress(response.hostAddress || "");
            setLotLocations(response.lotlocations || "");
            setStockInCo(response.stockInCo || "");
            setLotLocationOptionNumber(response.lotLocationOptionNumber || "");
            setLotLocationOptionDescription(response.lotLocationOptionDescription || "");
            setLastScannedOptionNumber(response.lastScannedOptionNumber || "");
            setLastScannedOptionDescription(response.lastScannedOptionDescription || "");
            setDealerCode(response.dealerCode || "");
            setIntegrationUrl(response.integrationURLForDominion || "");
            setAuthUrl(response.authenticationURL || "");
            setDealerNumber(response.dealerNumberID || "");
            setSenderName(response.senderNameCode || "");
            setDestinationName(response.destinationNameCode || "");
            setDownloadCustomerVehicles(response.downloadCustomerVehicles || false);
            setIgnoreZeroBalance(response.ignoreZeroBalance || false);
            setIsFtpEnabled(response.sftpFileBasedDMS);
            setIsLotlocationEnabled(response.enableLotLocationOptionNumber);
            setIsLastScannedEnabled(response.enableLastScannedOptionNumber);
            setFtpConfig(prev => ({
                ...prev,
                port: response.sftpPort || 22,
                defaultServer: response.scanSFTPFilebasedSettings ? 'Scan' : 'Custom'
            }));
            if (response.scanSFTPFilebasedSettings) {
                setScanFTPServer(true);
            }
            setCustomFTPConfig(prev => ({
                ...prev,
                host: response.sftpHost ?? '',
                port: response.sftpPort || 22,
                username: response.sftpUserName ?? '',
                password: response.sftpPassword ?? '',
                path: response.sftpDirectory ?? '',
            }));
            setScanFTPConfig(prev => ({
                ...prev,
                scanhost: response.scanSFTPHost ?? '',
                scanport: response.scanSFTPPort || 22,
                scanusername: response.scanSFTPUserName ?? '',
                scanpassword: response.scanSFTPPassword ?? '',
                scanpath: response.scanSFTPDirectory ?? ''
            }));
            setDirectorConfig(prev => ({
                ...prev,
                settings: response.directorSettings ?? false,
                name: response.directorName ?? ''
            }));
        } catch (error) {
            console.error('Error fetching dealership data:', error);
            sendAlert(1, 'Failed to fetch dealership data', 'Failure');
        }
    }, [dealerId, sendAlert]);

    // Form submission
    const handleSubmit = () => {
        let endpoint;
        if (!dealerId) {
            sendAlert(1, 'Dealer ID not found', 'Failure');
            return;
        }

        if (!directorName) {
            sendAlert(1, 'Director Name not found', 'Failure');
            return;
        }

        const formData: DMSFormData = {
            dmsId,
            companyID,
            enterprise,
            hostAddress,
            lotlocations: lotLocations,
            stockInCo,
            dealship: dealershipData,
            dmstype: dmsType,
            dealerCode,
            enableLotLocationOptionNumber: isLotlocationEnabled,
            lotLocationOptionNumber,
            lotLocationOptionDescription,
            enableLastScannedOptionNumber: isLastScannedEnabled,
            lastScannedOptionNumber,
            lastScannedOptionDescription,
            integrationURLForDominion: integrationUrl,
            dealerNumberID: dealerNumber,
            senderNameCode: senderName,
            destinationNameCode: destinationName,
            authenticationURL: authUrl,
            downloadCustomerVehicles,
            ignoreZeroBalance,
            sftpFileBasedDMS: isFtpEnabled,
            scanSFTPFilebasedSettings: ftpConfig.defaultServer === "Scan" ? true : false,
            sftpHost: customFTPConfig.host,
            sftpPort: customFTPConfig.port,
            sftpUserName: customFTPConfig.username,
            sftpPassword: customFTPConfig.password,
            sftpDirectory: customFTPConfig.path,
            scanSFTPHost: scanFTPConfig.scanhost,
            scanSFTPPort: scanFTPConfig.scanport,
            scanSFTPUserName: scanFTPConfig.scanusername,
            scanSFTPPassword: scanFTPConfig.scanpassword,
            scanSFTPDirectory: scanFTPConfig.scanpath,
            directorSettings: directorConfig.settings,
            directorName: directorConfig.name
        };

        if (userRole === "Director") {
            if (ftpConfig.defaultServer === "Custom" && !connectionAttempted) {
                console.log('inside the 1st if');
                sendAlert(1, "Please test the SFTP connection before saving", "Failure");
                return;
            }

            if (ftpConfig.defaultServer === "Custom" && connectionAttempted && !ftpConfig.connectionSuccess) {
                console.log('inside the 2nd if');
                sendAlert(1, "Make sure you can connect to the SFTP Server by clicking the test connection", "Failure");
                return;
            }

            endpoint = `DealerShip/DMSEditForReact?dealerIdSel=${directorName}-DMSConnectivity`;
            setShowNotification(true);
            setNotificationMessage("This will apply to all Dealerships, are you sure ?");
            setPendingFormData(formData);
            setPendingEndPoint(endpoint);
        } else {
            endpoint = dmsId === 0
                ? `DealerShip/DMSConnectivityPostForReact?dealerIdSel=${dealerId}-DMSConnectivity`
                : `DealerShip/DMSEditForReact?dealerIdSel=${dealerId}-DMSConnectivity`;
            if (directorConfig.settings) {
                setShowNotification(true);
                setNotificationMessage("A director has already configured your dealership, are you sure you want to change these settings ?");
                setPendingFormData(formData);
                setPendingEndPoint(endpoint);
            } else {
                handleApiCall(endpoint, formData);
            }
        }
    };

    const handleApiCall = async (endPoint: string, formData: any) => {
        try {
            const response = await GetModal('POST', endPoint, formData);
            if (response === "Success") {
                const message = dmsId === 0 ? 'DMS data created successfully' : 'DMS data updated successfully';
                sendAlert(0, message, 'Success');
            } else {
                const message = dmsId === 0 ? 'Failed to create the DMS Data' : 'Failed to update the DMS Data';
                sendAlert(1, message, 'Failure');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            sendAlert(1, 'An error occurred while processing your request', 'Failure');
        }
    }

    // Event handlers
    const handleDmsTypeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newType = parseInt(event.target.value);
        setDmsType(newType);
        // resetFormFields();
    };

    const handleFtpConfigChange = (field: keyof FTPConfig, value: string | number): void => {
        if (ftpConfig.defaultServer === 'Custom') {
            setCustomFTPConfig(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    useEffect(() => {
        fetchDealershipData();
    }, [fetchDealershipData]);

    // Render helpers
    const renderDmsTypeSelector = () => (
        <div className="grid grid-cols-2 gap-2 mt-2">
            <p className="sampleLabel">DMS Type</p>
            <div>
                {Object.entries(DMS_TYPE_LABELS).map(([value, label]) => (
                    <React.Fragment key={value}>
                        <input
                            type="radio"
                            id={label.toLowerCase().replace(' ', '')}
                            name="dms"
                            value={value}
                            checked={dmsType === parseInt(value)}
                            onChange={handleDmsTypeChange}
                        />
                        <label htmlFor={label.toLowerCase().replace(' ', '')} className="sampleLabel ml-1 mr-4">
                            {label}
                        </label>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );

    const renderCommonCheckboxes = () => (
        <div className="flex justify-around items-center mt-2">
            <div className="flex justify-start items-center mt-2">
                <input
                    type="checkbox"
                    checked={downloadCustomerVehicles}
                    onChange={() => setDownloadCustomerVehicles(!downloadCustomerVehicles)}
                />
                <p className="sampleLabel ml-2">Download Customer Vehicles</p>
            </div>
            <div className="flex justify-start items-center mt-2">
                <input
                    type="checkbox"
                    checked={ignoreZeroBalance}
                    onChange={() => setIgnoreZeroBalance(!ignoreZeroBalance)}
                />
                <p className="sampleLabel ml-1">Ignore Zero Balance</p>
            </div>
        </div>
    );

    const renderCdkDealerTrackFields = () => (
        <div className="modal-body">
            <div className="grid grid-cols-4 gap-2 mt-2 items-center justify-center">
                <p className="sampleLabel">Company ID</p>
                <input
                    className="textInput"
                    type="text"
                    placeholder="Company ID"
                    value={companyID}
                    onChange={(e) => setCompanyID(e.target.value)}
                />
                <p className="sampleLabel">Enterprise</p>
                <input
                    className="textInput"
                    type="text"
                    placeholder="Enterprise name"
                    value={enterprise}
                    onChange={(e) => setEnterprise(e.target.value)}
                />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2 items-center">
                <p className="sampleLabel">Host Address</p>
                <input
                    className="textInput"
                    type="url"
                    placeholder="https://host123.subhost.com/testpage"
                    value={hostAddress}
                    onChange={(e) => setHostAddress(e.target.value)}
                />
            </div>
            {renderCommonCheckboxes()}
        </div>
    );

    const renderDominionFields = () => (
        <div className="modal-body">
            {renderCommonCheckboxes()}
            <div className="grid grid-cols-4 gap-2 mt-2 items-center justify-center">
                <p className="sampleLabel">Integration URL</p>
                <input
                    className="textInput"
                    type="url"
                    placeholder="Integration URL"
                    value={integrationUrl}
                    onChange={(e) => setIntegrationUrl(e.target.value)}
                />
                <p className="sampleLabel">Authentication URL</p>
                <input
                    className="textInput"
                    type="url"
                    placeholder="Authentication URL"
                    value={authUrl}
                    onChange={(e) => setAuthUrl(e.target.value)}
                />
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2 items-center">
                <p className="sampleLabel">Dealer Number ID</p>
                <input
                    className="textInput"
                    type="text"
                    placeholder="Dealer Number ID"
                    value={dealerNumber}
                    onChange={(e) => setDealerNumber(e.target.value)}
                />
                <p className="sampleLabel">Sender Name Code</p>
                <input
                    className="textInput"
                    type="text"
                    placeholder="Sender Name Code"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2 items-center">
                <p className="sampleLabel">Destination Name Code</p>
                <input
                    className="textInput"
                    type="text"
                    placeholder="Destination Name Code"
                    value={destinationName}
                    onChange={(e) => setDestinationName(e.target.value)}
                />
            </div>
        </div>
    );

    const renderFtpFields = () => (
        <div className="modal-body">
            {userRole !== "Director" && (
                <div className="flex justify-around items-center mt-2">
                    <p className="sampleLabel">Company ID</p>
                    <input
                        className="textInput"
                        type="text"
                        placeholder="Company ID"
                        value={companyID}
                        onChange={(e) => setCompanyID(e.target.value)}
                    />
                </div>
            )}
            <div className="flex justify-around items-center mt-2">
                <input
                    type="checkbox"
                    checked={isFtpEnabled}
                    onChange={() => setIsFtpEnabled(!isFtpEnabled)}
                />
                <p className="sampleLabel">SFTP File-based DMS</p>
            </div>
            {isFtpEnabled && (
                <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg border border-gray-200 p-3 mt-4">
                    {renderFTPOptions()}
                    <hr className="mt-2" />
                    <div className="text-center">
                        <p className="sampleLabel">SFTP Server Details</p>
                    </div>
                    <hr className="mt-2" />
                    <div className="grid grid-cols-4 gap-2 mt-2 items-center">
                        <p className="sampleLabel">Host</p>
                        <input
                            type="text"
                            className="textInput"
                            placeholder="ftp.example.com"
                            value={ftpConfig.defaultServer === 'Scan' ? scanFTPConfig.scanhost : customFTPConfig.host}
                            onChange={(e) => handleFtpConfigChange('host', e.target.value)}
                            disabled={scanFTPServer}
                        />
                        <p className="sampleLabel">Port</p>
                        <input
                            type="number"
                            className="textInput"
                            placeholder="22"
                            value={ftpConfig.defaultServer === 'Scan' ? scanFTPConfig.scanport : customFTPConfig.port}
                            onChange={(e) => handleFtpConfigChange('port', parseInt(e.target.value) || 22)}
                            disabled={scanFTPServer}
                        />
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2 items-center">
                        <p className="sampleLabel">Username</p>
                        <input
                            type="text"
                            className="textInput"
                            placeholder="Username"
                            value={ftpConfig.defaultServer === 'Scan' ? scanFTPConfig.scanusername : customFTPConfig.username}
                            onChange={(e) => handleFtpConfigChange('username', e.target.value)}
                            disabled={scanFTPServer}
                        />
                        <p className="sampleLabel">Password</p>
                        <input
                            type="password"
                            className="textInput"
                            placeholder="Password"
                            value={ftpConfig.defaultServer === 'Scan' ? scanFTPConfig.scanpassword : customFTPConfig.password}
                            onChange={(e) => handleFtpConfigChange('password', e.target.value)}
                            disabled={scanFTPServer}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 items-center">
                        <p className="sampleLabel">File Upload Directory</p>
                        <input
                            type="text"
                            className="textInput"
                            placeholder="/upload/path"
                            value={ftpConfig.defaultServer === 'Scan' ? scanFTPConfig.scanpath : customFTPConfig.path}
                            onChange={(e) => handleFtpConfigChange('path', e.target.value)}
                            disabled={scanFTPServer}
                        />
                    </div>
                    {!scanFTPServer && (
                        <div className="flex justify-around items-center mt-4">
                            <button className="button-6" onClick={handleTestConnection} disabled={isTestingConnection}>
                                {isTestingConnection ? 'Testing ...' : 'Test Connection'}
                            </button>
                        </div>
                    )}
                    {ftpConfig.defaultServer === "Custom" && (
                        <div>
                            {ftpConfig.connectionSuccess ? (
                                <p className="pt-5 text-lg text-[#00FF00] text-center">
                                    Connection successful! SFTP Server is reachable.
                                </p>
                            ) : (
                                <>
                                    <p className="pt-5 text-lg text-[#FF0000] text-center">{ftpConnectionErrors}</p>
                                    {!connectionAttempted && (
                                        <p className="pt-5 text-lg text-[#FF0000] text-center">
                                            Make sure you can connect to the SFTP Server by clicking the test connection
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderCommonFields = () => (
        <div className="common-modal-body">
            <div className="grid grid-cols-4 gap-2 mt-2 items-center">
                <p className="sampleLabel">Lot Locations</p>
                <input
                    className="textInput"
                    type="text"
                    placeholder="10.4654375 N, 72.48944 E"
                    value={lotLocations}
                    onChange={(e) => setLotLocations(e.target.value)}
                />
                <p className="sampleLabel">Stock In Co.</p>
                <input
                    className="textInput"
                    type="number"
                    placeholder="15"
                    value={stockInCo}
                    onChange={(e) => setStockInCo(e.target.value)}
                />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-2">
                {/* Top row - checkboxes */}
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={isLotlocationEnabled}
                        onChange={() => setIsLotlocationEnabled(!isLotlocationEnabled)}
                    />
                    <p className="sampleLabel">Enable Lot Location</p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={isLastScannedEnabled}
                        onChange={() => setIsLastScannedEnabled(!isLastScannedEnabled)}
                    />
                    <p className="sampleLabel">Enable Last Scanned</p>
                </div>

                {/* Bottom row - boxes */}
                <div className="max-w-2xl bg-white rounded-lg shadow-lg border border-gray-200 p-3">
                    <div className="grid grid-cols-2 gap-2 items-center">
                        <p className="sampleLabel">Lot Location Option Number</p>
                        <input
                            className={`textInput ${!isLotlocationEnabled ? 'bg-gray-200 cursor-not-allowed' : ''}`}
                            type="number"
                            placeholder="54"
                            value={lotLocationOptionNumber}
                            onChange={(e) => setLotLocationOptionNumber(e.target.value)}
                            disabled={!isLotlocationEnabled}
                        />
                        <p className="sampleLabel">Lot Location Option Description</p>
                        <input
                            className={`textInput ${!isLotlocationEnabled ? 'bg-gray-200 cursor-not-allowed' : ''}`}
                            type="text"
                            placeholder="Lot location option Description"
                            value={lotLocationOptionDescription}
                            onChange={(e) => setLotLocationOptionDescription(e.target.value)}
                            disabled={!isLotlocationEnabled}
                        />
                    </div>
                </div>

                <div className="max-w-2xl bg-white rounded-lg shadow-lg border border-gray-200 p-3">
                    <div className="grid grid-cols-2 gap-2 items-center">
                        <p className="sampleLabel">Last Scanned Option Number</p>
                        <input
                            className={`textInput ${!isLastScannedEnabled ? 'bg-gray-200 cursor-not-allowed' : ''}`}
                            type="number"
                            placeholder="48"
                            value={lastScannedOptionNumber}
                            onChange={(e) => setLastScannedOptionNumber(e.target.value)}
                            disabled={!isLastScannedEnabled}
                        />
                        <p className="sampleLabel">Last Scanned Option Description</p>
                        <input
                            className={`textInput ${!isLastScannedEnabled ? 'bg-gray-200 cursor-not-allowed' : ''}`}
                            type="text"
                            placeholder="Last Scanned Option Description"
                            value={lastScannedOptionDescription}
                            onChange={(e) => setLastScannedOptionDescription(e.target.value)}
                            disabled={!isLastScannedEnabled}
                        />
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2 items-center">
                <p className="sampleLabel">Dealer Code (6 characters only)</p>
                <input
                    className="textInput"
                    type="text"
                    placeholder="ABC123"
                    maxLength={6}
                    value={dealerCode}
                    onChange={(e) => setDealerCode(e.target.value)}
                />
            </div>
        </div>
    );

    const renderDmsSpecificFields = () => {
        switch (dmsType) {
            case DMS_TYPES.CDK:
            case DMS_TYPES.DEALER_TRACK:
                return renderCdkDealerTrackFields();
            case DMS_TYPES.DOMINION:
                return renderDominionFields();
            case DMS_TYPES.FILE_BASED:
                return renderFtpFields();
            default:
                return null;
        }
    };

    const renderFTPOptions = () => (
        <div className="grid grid-cols-4 gap-2 mt-2 items-center">
            <label htmlFor="scan" className="sampleLabel">Scan SFTP Server</label>
            <input type="radio" id="scan" name="FTPServerSelection" value={ftpConfig.defaultServer} checked={ftpConfig.defaultServer === "Scan"} onChange={() => handleFTPServerChange('Scan')} />

            <label htmlFor="custom" className="sampleLabel">Customer SFTP Server</label>
            <input type="radio" id="custom" name="FTPServerSelection" value={ftpConfig.defaultServer} checked={ftpConfig.defaultServer === "Custom"} onChange={() => handleFTPServerChange('Custom')} />
        </div>
    );

    return (
        <div>
            {userRole !== "Director" && (
                <div className="modalTop-body mt-3">
                    <div className="grid grid-cols-2 gap-2 items-center">
                        <p className="sampleLabel">Dealership Name</p>
                        <input
                            className="textInput"
                            type="text"
                            placeholder="Dealership Name"
                            value={dealershipName}
                            readOnly
                        />
                    </div>
                    {renderDmsTypeSelector()}
                    <hr className="mt-2" />
                </div>
            )}

            <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                {userRole === "Director" ? (
                    <>
                        {renderFtpFields()}
                        <p className="pt-5 text-lg text-[#FF0000] font-bold text-center">Note: These changes will apply to all Dealerships</p>
                    </>
                ) : (
                    <>
                        {renderDmsSpecificFields()}
                        {renderCommonFields()}
                    </>
                )}
                {showNotification && (
                    <div>
                        <NotifyComponent message={notificationMessage} title="Operation Confirmation" show={showNotification} onOk={handleOk} onCancel={handleCancel} />
                    </div>
                )}
                <div className="modal-footer">
                    <div className="flex justify-around items-center mt-4">
                        <button className="button-6" onClick={handleSubmit}>
                            {dmsId === 0 ? 'Create' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DmsConnectivity;